import {
  DRUM_CHANNEL,
  getInstrumentMeta,
  type Midi,
  type MidiChannel,
  type MidiNote,
} from "@midi-fantom-utils/midi-core";
import { resolveProgramAt } from "../playback.js";

/**
 * Дорожка (лейн) для отображения таймлайна «как Bitwig».
 *
 * Дорожка = (канал, GM-программа). Один MIDI-канал может в разное время
 * использовать разные программы (несколько program-change) — тогда ноты
 * канала разбиваются на несколько дорожек по программе, активной на тик
 * ноты (см. `resolveProgramAt`). Канал 9 (ударные) — всегда одна дорожка
 * с `program = 0` (GM drum kit).
 *
 * `buildTrackLanes` — чистая функция без React/fs; удобна для тестов и
 * переиспользования. Имя дорожки по умолчанию — GM-имя (`getInstrumentMeta`),
 * ударные — `"Drums"`; web передаёт SF2-имена через `nameFor`.
 */

export type TrackLane = {
  /** Стабильный id: `${channel}:${program}`. */
  id: string;
  /** MIDI-канал 0..15 (источник для срезов/экспорта). */
  channel: number;
  /** GM-программа 0..127; для канала 9 — всегда 0 (drum kit). */
  program: number;
  /** true для ударной дорожки (канал 9 или percussion-трек). */
  isDrum: boolean;
  /** Имя инструмента для отображения. */
  name: string;
  noteCount: number;
  notes: ReadonlyArray<MidiNote>;
};

/** Провайдер имени дорожки (например, SF2-пресет из program-map). */
export type TrackLaneNameFor = (channel: number, program: number) => string;

type ProgramChange = { ticks: number; value: number };

const clampProgram = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(127, Math.round(value)));
};

const DEFAULT_DRUM_NAME = "Drums";

const pcsFallback = (pcs: ProgramChange[] | undefined): number => {
  if (pcs && pcs.length > 0 && Number.isFinite(pcs[0]!.value)) {
    return pcs[0]!.value;
  }
  return 0;
};

export const buildTrackLanes = (
  midi: Midi,
  nameFor?: TrackLaneNameFor
): TrackLane[] => {
  // Program changes, сгруппированные по каналу и отсортированные по тикам.
  const programChangesByChannel = new Map<number, ProgramChange[]>();
  for (const track of midi.tracks) {
    for (const pc of track.programChanges) {
      const ch = pc.channel ?? track.channel;
      if (!Number.isFinite(pc.ticks) || !Number.isFinite(pc.value)) continue;
      const list = programChangesByChannel.get(ch) ?? [];
      list.push({ ticks: pc.ticks, value: pc.value });
      programChangesByChannel.set(ch, list);
    }
  }
  for (const list of programChangesByChannel.values()) {
    list.sort((a, b) => a.ticks - b.ticks);
  }

  // defaultProgram на канал: инструмент канала (`track.instrument.number`,
  // для per-channel раскладки = первый PC канала), иначе первый PC из
  // `programChanges`. Это «стартовый» инструмент для нот до первого PC.
  const defaultProgramByChannel = new Map<number, number>();
  for (const track of midi.tracks) {
    const ch = track.channel;
    const fallback = pcsFallback(programChangesByChannel.get(ch));
    const instrumentNumber = Number.isFinite(track.instrument.number)
      ? track.instrument.number
      : fallback;
    defaultProgramByChannel.set(ch, instrumentNumber);
  }

  // Собираем дорожки в порядке появления: каналы по возрастанию (у выхода
  // `toMultiTrackFormat` треки уже отсортированы по каналу), внутри канала —
  // по первому появлению программы среди нот.
  type DraftLane = Omit<TrackLane, "notes" | "noteCount"> & {
    notes: MidiNote[];
    noteCount: number;
  };
  const lanes = new Map<string, DraftLane>();

  const isSingleChannelTrack = (track: Midi["tracks"][number]): boolean =>
    track.notes.every((n) => (n.channel ?? track.channel) === track.channel);

  for (const track of midi.tracks) {
    const trackChannel = track.channel;
    const pcs = programChangesByChannel.get(trackChannel) ?? [];
    const defaultProgram = clampProgram(
      defaultProgramByChannel.get(trackChannel) ?? 0
    );
    const isDrumTrack =
      isSingleChannelTrack(track) && track.instrument.percussion === true;

    for (const note of track.notes) {
      const ch = note.channel ?? trackChannel;
      if (ch < 0 || ch > 15) continue;
      const isDrum = isDrumTrack || ch === DRUM_CHANNEL;
      const program = isDrum
        ? 0
        : resolveProgramAt(pcs, note.ticks, false, defaultProgram);
      const key = `${ch}:${program}`;
      let lane = lanes.get(key);
      if (!lane) {
        const name = nameFor
          ? nameFor(ch, program) || defaultLaneName(ch, program, isDrum)
          : defaultLaneName(ch, program, isDrum);
        lane = {
          id: key,
          channel: ch,
          program,
          isDrum,
          name,
          noteCount: 0,
          notes: [],
        };
        lanes.set(key, lane);
      }
      lane.notes.push(note);
      lane.noteCount += 1;
    }
  }

  // Сортировка: по каналу, внутри канала — по порядку первого появления.
  const out: TrackLane[] = [];
  const channels = [...lanes.values()]
    .map((l) => l.channel)
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort((a, b) => a - b);
  for (const ch of channels) {
    for (const lane of lanes.values()) {
      if (lane.channel === ch) {
        out.push({ ...lane, notes: lane.notes, noteCount: lane.notes.length });
      }
    }
  }
  return out;
};

const defaultLaneName = (
  channel: number,
  program: number,
  isDrum: boolean
): string => {
  if (isDrum) return DEFAULT_DRUM_NAME;
  return getInstrumentMeta(program, channel as MidiChannel).name;
};

/**
 * Убирает ноты дорожек, id которых входят в `mutedLaneIds` (формат
 * `${channel}:${program}` — тот же, что у `TrackLane.id`), из `Midi`.
 *
 * Используется для «Mute» в лейновом таймлайне: muted-дорожка не должна
 * звучать в плеере и не должна попадать в срезы/экспорт паттернов. Программа
 * каждой ноты резолвится так же, как в `buildTrackLanes` (`resolveProgramAt`
 * по program-change канала) — чтобы id дорожек совпадали. CC/PC/pitch bend
 * канала не трогаем: mute глушит именно ноты инструмента, а не весь канал.
 *
 * Чистая функция: возвращает исходный `midi` (ref-равенство) при пустом
 * `mutedLaneIds`.
 */
export const dropMutedLaneNotes = (
  midi: Midi,
  mutedLaneIds: ReadonlySet<string>
): Midi => {
  if (mutedLaneIds.size === 0) return midi;

  let changed = false;
  const tracks = midi.tracks.map((track) => {
    if (track.notes.length === 0) return track;
    const ch = track.channel;
    const pcs = (track.programChanges
      .filter((pc) => Number.isFinite(pc.ticks) && Number.isFinite(pc.value))
      .map((pc) => ({ ticks: pc.ticks, value: pc.value }))
      .sort((a, b) => a.ticks - b.ticks)) as ProgramChange[];
    const fallback = pcs[0]?.value ?? 0;
    const defaultProgram = Number.isFinite(track.instrument.number)
      ? track.instrument.number
      : fallback;
    const isSingleChannel = track.notes.every(
      (n) => (n.channel ?? ch) === ch
    );
    const isDrumTrack = isSingleChannel && track.instrument.percussion === true;

    const kept = track.notes.filter((note) => {
      const noteChannel = note.channel ?? ch;
      const isDrum = isDrumTrack || noteChannel === DRUM_CHANNEL;
      const program = isDrum
        ? 0
        : resolveProgramAt(pcs, note.ticks, false, defaultProgram);
      return !mutedLaneIds.has(`${noteChannel}:${program}`);
    });
    if (kept.length === track.notes.length) return track;
    changed = true;
    return { ...track, notes: kept };
  });

  if (!changed) return midi;
  return { ...midi, tracks };
};
